import React, { useLayoutEffect, useRef, useState } from 'react';
import { BodyM, cn, Icon } from '@lqa-frontend/ui-system';
import { SimpleTooltip } from '../tooltip';

export interface IBreadcrumbItem {
	name: string;
	path?: string;
	isDisabled?: boolean;
}

export interface IBreadcrumbProps {
	items: IBreadcrumbItem[];
	onItemClick?: (item: IBreadcrumbItem) => void;
	className?: string;
}

const MAX_VISIBLE_ITEMS = 5;
const TRAILING_ITEMS = 3;

type DisplayEntry =
	{ type: 'item'; item: IBreadcrumbItem; index: number } | { type: 'ellipsis' };

function getDisplayEntries(items: IBreadcrumbItem[]): DisplayEntry[] {
	if (items.length <= MAX_VISIBLE_ITEMS) {
		return items.map((item, index) => ({ type: 'item', item, index }));
	}

	return [
		{ type: 'item', item: items[0], index: 0 },
		{ type: 'ellipsis' },
		...items.slice(-TRAILING_ITEMS).map((item, trailingIndex) => ({
			type: 'item' as const,
			item,
			index: items.length - TRAILING_ITEMS + trailingIndex,
		})),
	];
}

interface IBreadcrumbLabelProps {
	item: IBreadcrumbItem;
	index: number;
	isLast: boolean;
	isClickable: boolean;
	onItemClick?: (item: IBreadcrumbItem) => void;
}

function BreadcrumbLabel({
	item,
	index,
	isLast,
	isClickable,
	onItemClick,
}: IBreadcrumbLabelProps) {
	const labelRef = useRef<HTMLButtonElement & HTMLParagraphElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useLayoutEffect(() => {
		const labelElement = labelRef.current;
		if (labelElement) {
			setIsTruncated(labelElement.scrollWidth > labelElement.clientWidth);
		}
	}, [item.name]);

	const truncateClasses = 'block max-w-240 truncate';

	const labelContent = isClickable ? (
		<button
			ref={labelRef}
			type="button"
			onClick={() => onItemClick?.(item)}
			disabled={item.isDisabled}
			className={cn(
				'text-14 text-text-secondary hover:text-text-primary transition-colors capitalize outline-none focus-visible:ring-2 focus-visible:ring-interactive focus-visible:ring-offset-2 rounded-s cursor-pointer',
				truncateClasses,
				item.isDisabled && 'opacity-50 cursor-not-allowed'
			)}
			data-testid={`breadcrumb-button-${index}`}
		>
			{item.name}
		</button>
	) : (
		<BodyM
			ref={labelRef}
			className={cn(
				'capitalize',
				truncateClasses,
				isLast ? 'text-text-primary' : 'text-text-secondary'
			)}
			data-testid={`breadcrumb-text-${index}`}
		>
			{item.name}
		</BodyM>
	);

	if (!isTruncated) {
		return labelContent;
	}

	return <SimpleTooltip content={item.name}>{labelContent}</SimpleTooltip>;
}

function BreadcrumbSeparator() {
	return (
		<Icon
			name="chevronRight"
			size="s"
			className="mx-20 text-neutral"
			aria-hidden="true"
		/>
	);
}

function Breadcrumb({ items, onItemClick, className }: IBreadcrumbProps) {
	if (!items || items.length === 0) {
		return null;
	}

	const entries = getDisplayEntries(items);

	return (
		<nav
			className={cn('flex items-center', className)}
			aria-label="Breadcrumb"
			data-testid="breadcrumb"
		>
			<ol className="flex items-center">
				{entries.map((entry, entryIndex) => {
					const isLastEntry = entryIndex === entries.length - 1;

					if (entry.type === 'ellipsis') {
						return (
							<li
								key="ellipsis"
								className="flex items-center"
								data-testid="breadcrumb-ellipsis"
							>
								<span
									className="text-14 text-text-secondary select-none leading-none"
									aria-label="Collapsed items"
								>
									...
								</span>
								{!isLastEntry && <BreadcrumbSeparator />}
							</li>
						);
					}

					const { item, index } = entry;
					const isLast = index === items.length - 1;
					const isClickable = !item.isDisabled && !!item.path && !!onItemClick;

					return (
						<li key={`${item.name}-${index}`} className="flex items-center">
							<BreadcrumbLabel
								item={item}
								index={index}
								isLast={isLast}
								isClickable={isClickable}
								onItemClick={onItemClick}
							/>
							{!isLastEntry && <BreadcrumbSeparator />}
						</li>
					);
				})}
			</ol>
		</nav>
	);
}

export { Breadcrumb };
